// Search Index Data for Command Palette

export interface SearchItem {
  id: string;
  title: string;
  subtitle?: string;
  category: 'NAVIGATION' | 'INDICATOR' | 'SECTOR' | 'COUNTRY' | 'EVENT' | 'METRIC';
  value?: string | number;
  change?: string;
  action: {
    type: 'navigate' | 'modal' | 'highlight';
    target: string;
  };
  keywords: string[];
  icon?: string;
}

export const searchIndex: SearchItem[] = [
  // NAVIGATION - View shortcuts
  {
    id: 'nav-overview',
    title: 'Overview Dashboard',
    subtitle: 'Main market summary',
    category: 'NAVIGATION',
    action: { type: 'navigate', target: 'overview' },
    keywords: ['overview', 'dashboard', 'home', 'summary', 'main'],
    icon: 'LayoutDashboard',
  },
  {
    id: 'nav-macro',
    title: 'Macro Indicators',
    subtitle: 'GDP, inflation, fiscal data',
    category: 'NAVIGATION',
    action: { type: 'navigate', target: 'macro' },
    keywords: ['macro', 'gdp', 'inflation', 'economy', 'fiscal', 'debt'],
    icon: 'TrendingUp',
  },
  {
    id: 'nav-fdi',
    title: 'FDI Analysis',
    subtitle: 'Foreign direct investment',
    category: 'NAVIGATION',
    action: { type: 'navigate', target: 'fdi' },
    keywords: ['fdi', 'foreign', 'investment', 'inflows', 'capital'],
    icon: 'Building',
  },
  {
    id: 'nav-sectors',
    title: 'Sector Analysis',
    subtitle: 'FinTech, AI, Web3, E-Commerce',
    category: 'NAVIGATION',
    action: { type: 'navigate', target: 'sectors' },
    keywords: ['sectors', 'fintech', 'ai', 'web3', 'ecommerce', 'industry'],
    icon: 'Cpu',
  },
  {
    id: 'nav-peers',
    title: 'Peer Comparison',
    subtitle: 'Emerging markets ranking',
    category: 'NAVIGATION',
    action: { type: 'navigate', target: 'peers' },
    keywords: ['peers', 'comparison', 'ranking', 'countries', 'emerging'],
    icon: 'BarChart3',
  },
  {
    id: 'nav-events',
    title: 'Events & Calendar',
    subtitle: 'Catalysts, timeline, risks',
    category: 'NAVIGATION',
    action: { type: 'navigate', target: 'events' },
    keywords: ['events', 'calendar', 'timeline', 'catalyst', 'wto'],
    icon: 'Clock',
  },

  // INDICATORS - Key metrics
  {
    id: 'ind-gdp',
    title: 'GDP',
    subtitle: 'Gross Domestic Product',
    category: 'INDICATOR',
    value: '$105B',
    change: '+6.2%',
    action: { type: 'navigate', target: 'macro' },
    keywords: ['gdp', 'gross', 'domestic', 'product', 'economy', 'size'],
  },
  {
    id: 'ind-gdp-growth',
    title: 'GDP Growth',
    subtitle: 'Year-over-year growth rate',
    category: 'INDICATOR',
    value: '6.2%',
    change: '+0.3%',
    action: { type: 'navigate', target: 'macro' },
    keywords: ['gdp', 'growth', 'rate', 'yoy', 'expansion'],
  },
  {
    id: 'ind-gdp-capita',
    title: 'GDP Per Capita',
    subtitle: 'Income per person',
    category: 'INDICATOR',
    value: '$3,500',
    change: '+20.7%',
    action: { type: 'navigate', target: 'macro' },
    keywords: ['gdp', 'capita', 'income', 'per', 'person', 'wealth'],
  },
  {
    id: 'ind-inflation',
    title: 'Inflation (CPI)',
    subtitle: 'Consumer price index',
    category: 'INDICATOR',
    value: '9.0%',
    change: '-1.4%',
    action: { type: 'navigate', target: 'macro' },
    keywords: ['inflation', 'cpi', 'prices', 'consumer', 'cost'],
  },
  {
    id: 'ind-fdi',
    title: 'FDI Inflows',
    subtitle: 'Foreign direct investment 2024',
    category: 'INDICATOR',
    value: '$11.9B',
    change: '+53.6%',
    action: { type: 'navigate', target: 'fdi' },
    keywords: ['fdi', 'foreign', 'direct', 'investment', 'inflows'],
  },
  {
    id: 'ind-reserves',
    title: 'FX Reserves',
    subtitle: 'Foreign exchange reserves',
    category: 'INDICATOR',
    value: '$49.7B',
    change: '+21%',
    action: { type: 'navigate', target: 'macro' },
    keywords: ['reserves', 'fx', 'foreign', 'exchange', 'central', 'bank'],
  },
  {
    id: 'ind-population',
    title: 'Population',
    subtitle: 'Total population',
    category: 'INDICATOR',
    value: '37.1M',
    change: '+1.5%',
    action: { type: 'navigate', target: 'macro' },
    keywords: ['population', 'people', 'demographic', 'census'],
  },
  {
    id: 'ind-median-age',
    title: 'Median Age',
    subtitle: 'Population median age',
    category: 'INDICATOR',
    value: '27 years',
    action: { type: 'navigate', target: 'macro' },
    keywords: ['median', 'age', 'young', 'demographic', 'workforce'],
  },
  {
    id: 'ind-debt',
    title: 'Public Debt',
    subtitle: 'Government debt to GDP',
    category: 'INDICATOR',
    value: '33%',
    change: '-2%',
    action: { type: 'navigate', target: 'macro' },
    keywords: ['debt', 'public', 'government', 'fiscal', 'borrowing'],
  },
  {
    id: 'ind-rating-sp',
    title: 'S&P Rating',
    subtitle: 'Sovereign credit rating',
    category: 'INDICATOR',
    value: 'BB',
    change: '↑ from BB-',
    action: { type: 'navigate', target: 'overview' },
    keywords: ['rating', 'sp', 'credit', 'sovereign', 'upgrade'],
  },
  {
    id: 'ind-rating-fitch',
    title: 'Fitch Rating',
    subtitle: 'Sovereign credit rating',
    category: 'INDICATOR',
    value: 'BB',
    change: '↑ from BB-',
    action: { type: 'navigate', target: 'overview' },
    keywords: ['rating', 'fitch', 'credit', 'sovereign', 'upgrade'],
  },
  {
    id: 'ind-rating-moodys',
    title: "Moody's Rating",
    subtitle: 'Sovereign credit rating',
    category: 'INDICATOR',
    value: 'Ba3',
    change: 'Outlook POS',
    action: { type: 'navigate', target: 'overview' },
    keywords: ['rating', 'moodys', 'moody', 'credit', 'sovereign', 'outlook'],
  },
  {
    id: 'ind-score',
    title: 'Opportunity Score',
    subtitle: 'Natex proprietary index',
    category: 'INDICATOR',
    value: '88/100',
    change: 'STRONG BUY',
    action: { type: 'navigate', target: 'natex-score' },
    keywords: ['opportunity', 'score', 'index', 'rating', 'signal', 'buy', 'natex'],
  },
  {
    id: 'ind-digital-payments',
    title: 'Digital Payments',
    subtitle: 'Monthly transaction volume',
    category: 'INDICATOR',
    value: '$10.2B/mo',
    change: '+45%',
    action: { type: 'navigate', target: 'overview' },
    keywords: ['digital', 'payments', 'fintech', 'mobile', 'transactions'],
  },
  {
    id: 'ind-unicorns',
    title: 'Unicorns',
    subtitle: 'Billion-dollar startups',
    category: 'INDICATOR',
    value: '2',
    change: '$2.5B combined',
    action: { type: 'navigate', target: 'overview' },
    keywords: ['unicorn', 'startup', 'uzum', 'valuation', 'billion'],
  },

  // SECTORS
  {
    id: 'sec-fintech',
    title: 'FinTech Sector',
    subtitle: '$6.22B TAM, 11.1% CAGR',
    category: 'SECTOR',
    value: 'BUY',
    action: { type: 'navigate', target: 'sectors' },
    keywords: ['fintech', 'financial', 'technology', 'payments', 'banking', 'digital'],
  },
  {
    id: 'sec-ai',
    title: 'AI / Machine Learning',
    subtitle: '$0.15B TAM, 52% CAGR',
    category: 'SECTOR',
    value: 'STRONG BUY',
    action: { type: 'navigate', target: 'sectors' },
    keywords: ['ai', 'artificial', 'intelligence', 'machine', 'learning', 'ml'],
  },
  {
    id: 'sec-web3',
    title: 'Web3 / Crypto',
    subtitle: '$0.04B TAM, 63% CAGR',
    category: 'SECTOR',
    value: 'STRONG BUY',
    action: { type: 'navigate', target: 'sectors' },
    keywords: ['web3', 'crypto', 'blockchain', 'defi', 'nft', 'bitcoin'],
  },
  {
    id: 'sec-ecommerce',
    title: 'E-Commerce',
    subtitle: '$3.20B TAM, 23.7% CAGR',
    category: 'SECTOR',
    value: 'HOLD',
    action: { type: 'navigate', target: 'sectors' },
    keywords: ['ecommerce', 'commerce', 'retail', 'online', 'marketplace'],
  },

  // COUNTRIES (Central Asia & Silk Road Peers)
  {
    id: 'country-uzb',
    title: 'Uzbekistan',
    subtitle: 'Score: 88 | Rank: #1',
    category: 'COUNTRY',
    value: 'BB',
    action: { type: 'navigate', target: 'peers' },
    keywords: ['uzbekistan', 'uzb', 'tashkent', 'central', 'asia'],
  },
  {
    id: 'country-kaz',
    title: 'Kazakhstan',
    subtitle: 'Score: 68 | Rank: #2',
    category: 'COUNTRY',
    value: 'BBB-',
    action: { type: 'navigate', target: 'peers' },
    keywords: ['kazakhstan', 'kaz', 'astana', 'central', 'asia'],
  },
  {
    id: 'country-kgz',
    title: 'Kyrgyzstan',
    subtitle: 'Score: 52 | Rank: #5',
    category: 'COUNTRY',
    value: 'B-',
    action: { type: 'navigate', target: 'peers' },
    keywords: ['kyrgyzstan', 'kgz', 'bishkek', 'central', 'asia'],
  },
  {
    id: 'country-tjk',
    title: 'Tajikistan',
    subtitle: 'Score: 48 | Rank: #6',
    category: 'COUNTRY',
    value: 'B-',
    action: { type: 'navigate', target: 'peers' },
    keywords: ['tajikistan', 'tjk', 'dushanbe', 'central', 'asia'],
  },
  {
    id: 'country-tkm',
    title: 'Turkmenistan',
    subtitle: 'Score: 45 | Rank: #8',
    category: 'COUNTRY',
    value: 'NR',
    action: { type: 'navigate', target: 'peers' },
    keywords: ['turkmenistan', 'tkm', 'ashgabat', 'central', 'asia'],
  },
  {
    id: 'country-aze',
    title: 'Azerbaijan',
    subtitle: 'Score: 62 | Rank: #4',
    category: 'COUNTRY',
    value: 'BB+',
    action: { type: 'navigate', target: 'peers' },
    keywords: ['azerbaijan', 'aze', 'baku', 'caucasus', 'silk', 'road'],
  },
  {
    id: 'country-geo',
    title: 'Georgia',
    subtitle: 'Score: 76 | Rank: #3',
    category: 'COUNTRY',
    value: 'BB',
    action: { type: 'navigate', target: 'peers' },
    keywords: ['georgia', 'geo', 'tbilisi', 'caucasus', 'silk', 'road'],
  },
  {
    id: 'country-mng',
    title: 'Mongolia',
    subtitle: 'Score: 58 | Rank: #7',
    category: 'COUNTRY',
    value: 'B',
    action: { type: 'navigate', target: 'peers' },
    keywords: ['mongolia', 'mng', 'ulaanbaatar', 'east', 'asia'],
  },

  // EVENTS
  {
    id: 'evt-wto',
    title: 'WTO Accession',
    subtitle: '93 days | March 2026',
    category: 'EVENT',
    value: 'CRITICAL',
    action: { type: 'navigate', target: 'events' },
    keywords: ['wto', 'world', 'trade', 'organization', 'accession', 'membership'],
  },
  {
    id: 'evt-bank-ipo',
    title: 'Bank Privatizations',
    subtitle: '200 days | 2026',
    category: 'EVENT',
    value: 'HIGH',
    action: { type: 'navigate', target: 'events' },
    keywords: ['bank', 'privatization', 'ipo', 'asaka', 'ipoteka'],
  },
  {
    id: 'evt-eurobond',
    title: 'Sovereign Eurobond',
    subtitle: '150 days | Q2 2025',
    category: 'EVENT',
    value: 'HIGH',
    action: { type: 'navigate', target: 'events' },
    keywords: ['eurobond', 'bond', 'sovereign', 'debt', 'issuance'],
  },
  {
    id: 'evt-upgrade',
    title: 'Potential BB+ Upgrade',
    subtitle: '270 days | H2 2025',
    category: 'EVENT',
    value: 'HIGH',
    action: { type: 'navigate', target: 'events' },
    keywords: ['upgrade', 'rating', 'bb+', 'investment', 'grade'],
  },
  {
    id: 'evt-it-park',
    title: 'IT Park Phase 2',
    subtitle: '110 days | April 2025',
    category: 'EVENT',
    value: 'MEDIUM',
    action: { type: 'navigate', target: 'events' },
    keywords: ['it', 'park', 'tech', 'hub', 'expansion'],
  },

  // METRICS (Quick lookups)
  {
    id: 'met-wto-progress',
    title: 'WTO Progress',
    subtitle: 'Bilateral deals completed',
    category: 'METRIC',
    value: '31/34',
    change: '91%',
    action: { type: 'navigate', target: 'overview' },
    keywords: ['wto', 'bilateral', 'progress', 'deals', 'trade'],
  },
  {
    id: 'met-fdi-sources',
    title: 'Top FDI Source',
    subtitle: 'Largest investor country',
    category: 'METRIC',
    value: 'China',
    change: '25.6%',
    action: { type: 'navigate', target: 'fdi' },
    keywords: ['china', 'fdi', 'source', 'investor', 'largest'],
  },
  {
    id: 'met-fintech-users',
    title: 'FinTech Users',
    subtitle: 'Active fintech users',
    category: 'METRIC',
    value: '17M',
    action: { type: 'navigate', target: 'overview' },
    keywords: ['fintech', 'users', 'customers', 'adoption'],
  },
  {
    id: 'met-deal-pipeline',
    title: 'Deal Pipeline',
    subtitle: 'Active investment deals',
    category: 'METRIC',
    value: '$6.62B',
    change: '8 deals',
    action: { type: 'navigate', target: 'events' },
    keywords: ['deal', 'pipeline', 'investment', 'transactions'],
  },
  {
    id: 'met-reform-progress',
    title: 'Reform Progress',
    subtitle: 'Reforms on track',
    category: 'METRIC',
    value: '4/6',
    change: '67%',
    action: { type: 'navigate', target: 'events' },
    keywords: ['reform', 'progress', 'track', 'policy'],
  },
];

// Category display config
export const categoryConfig: Record<string, { label: string; color: string; icon: string }> = {
  NAVIGATION: { label: 'Go to', color: 'text-data-blue', icon: 'ArrowRight' },
  INDICATOR: { label: 'Indicators', color: 'text-data-green', icon: 'TrendingUp' },
  SECTOR: { label: 'Sectors', color: 'text-data-amber', icon: 'Cpu' },
  COUNTRY: { label: 'Countries', color: 'text-purple-400', icon: 'Globe' },
  EVENT: { label: 'Events', color: 'text-cyan-400', icon: 'Calendar' },
  METRIC: { label: 'Metrics', color: 'text-gray-400', icon: 'Hash' },
};
